import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';

/**
 * TileSettingsModal — owns draft state internally.
 *
 * Props:
 *   open            boolean                           — controlled open state
 *   onOpenChange    (open: boolean) => void           — open state setter
 *   title           string
 *   getInitialValues () => object                     — called once when modal opens
 *   onApply         (values: object) => void          — called with snapshot on Apply
 *   applyButtonText string                            — default "Apply"
 *   isApplyDisabled boolean | (values) => boolean     — can be a predicate over draft
 *   showApplyButton boolean                           — default true
 *   children        (renderProps) => ReactNode        — render prop: { values, setField, update }
 */
const TileSettingsModal = ({
  open,
  onOpenChange,
  // Legacy API (backwards-compat)
  isOpen,
  onClose,
  title,
  getInitialValues,
  onApply,
  applyButtonText = 'Apply',
  isApplyDisabled = false,
  showApplyButton = true,
  children,
}) => {
  const resolvedOpen = open ?? isOpen ?? false;
  const resolvedOnOpenChange =
    onOpenChange ?? ((val) => { if (!val && onClose) onClose(); });

  const [draft, setDraft] = useState(null);

  // Snapshot committed values when modal opens; clear when it closes.
  // Intentionally exclude getInitialValues from deps to avoid re-snapshotting mid-edit.
  useEffect(() => {
    if (resolvedOpen) {
      setDraft(getInitialValues ? getInitialValues() : {});
    } else {
      setDraft(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedOpen]);

  const setField = (key, val) =>
    setDraft((prev) => ({ ...prev, [key]: val }));

  const update = (fn) => setDraft(fn);

  const handleApply = () => {
    const snapshot = draft; // capture before close clears it
    onApply?.(snapshot);        // 1. commit first
    resolvedOnOpenChange(false); // 2. then close
  };

  const handleCancel = () => {
    resolvedOnOpenChange(false);
  };

  const applyDisabled =
    typeof isApplyDisabled === 'function'
      ? draft == null || isApplyDisabled(draft)
      : isApplyDisabled;

  return (
    <Dialog open={resolvedOpen} onOpenChange={resolvedOnOpenChange}>
      <DialogContent
        className="max-w-2xl"
        onMouseDown={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
        onTouchStart={(e) => e.stopPropagation()}
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        <div className="p-6 space-y-4 overflow-y-auto flex-grow">
          {draft !== null && typeof children === 'function'
            ? children({ values: draft, setField, update })
            : children}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
          {showApplyButton && (
            <Button onClick={handleApply} disabled={applyDisabled}>
              {applyButtonText}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TileSettingsModal;
